import { Ban } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { cn } from "@/lib/utils";

/**
 * Confirmation for "delete for everyone" — two transports behind one dialog:
 *   - `variant="whatsapp"`: retract one of our messages from the patient's phone
 *     (POST /api/threads/{id}/messages/{id}/revoke). The bubble stays, struck through.
 *   - `variant="internal"`: unsend one of your own messages in a Healo conversation (staff Chats or a
 *     Healo-app patient thread) — it vanishes for everyone
 *     (POST /api/threads/{id}/messages/{id}/unsend). The bubble disappears for every member.
 *
 * The preview matters: consecutive sends merge into one bubble, so the dialog has to show WHICH
 * message is about to be pulled back. The caveat matters just as much — WhatsApp refuses
 * retractions past its ~2-day window, and when it does the message stays in the patient's chat and
 * the bubble says so; an internal unsend has no verdict to wait for, but no undo either.
 *
 * @param {object} props
 * @param {boolean} props.open
 * @param {(open: boolean) => void} props.onOpenChange
 * @param {() => void | Promise<void>} props.onConfirmYes
 * @param {string} [props.preview] the message text being retracted
 * @param {"whatsapp"|"internal"} [props.variant] which description to show (default WhatsApp)
 */
export function RevokeMessageDialog({ open, onOpenChange, onConfirmYes, preview, variant = "whatsapp" }) {
  const text = String(preview || "").trim();
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className={cn("gap-0 overflow-hidden p-0 sm:max-w-md", "border-border/80 shadow-lg")}>
        <div className="border-b border-border/60 bg-muted/30 px-6 pb-4 pt-6">
          <DialogHeader className="space-y-4 text-left">
            <div
              className="flex h-12 w-12 items-center justify-center rounded-2xl bg-destructive/15 text-destructive ring-1 ring-destructive/20 dark:bg-destructive/10 dark:ring-destructive/25"
              aria-hidden
            >
              <Ban className="h-6 w-6" strokeWidth={2.25} />
            </div>
            <div className="space-y-1.5">
              <DialogTitle className="text-xl font-semibold tracking-tight">
                Delete this message for everyone?
              </DialogTitle>
              <DialogDescription className="text-[15px] leading-relaxed text-muted-foreground">
                {variant === "internal" ? (
                  <>
                    It disappears from this conversation for everyone — there&apos;s no undo. Anyone who
                    already read it may have seen it.
                  </>
                ) : (
                  <>
                    We'll ask WhatsApp to remove it from the patient's chat. It stays here, struck
                    through, so the conversation keeps its record. WhatsApp refuses to retract older
                    messages — if that happens the patient still sees this one, and the message will
                    say so.
                  </>
                )}
              </DialogDescription>
            </div>
          </DialogHeader>
          {/* Clamp on the inner element: `overflow-hidden` clips at the padding box, so clamping the
              padded box itself bleeds a sliced half-line into its bottom padding. */}
          {text ? (
            <div className="mt-4 rounded-md border border-border/60 bg-background/60 px-3 py-2">
              <p className="line-clamp-3 wrap-anywhere text-[13px] text-muted-foreground">{text}</p>
            </div>
          ) : null}
        </div>
        <DialogFooter className="flex flex-row items-center justify-end gap-2 border-t border-border/60 bg-background px-6 py-4 sm:justify-end">
          <Button type="button" variant="outline" className="min-w-18" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            type="button"
            variant="destructive"
            className="min-w-18"
            onClick={() => {
              void onConfirmYes();
            }}
          >
            Delete for everyone
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
